<template>
  <div class="manual-page">
    <!-- Video Section (placeholder for future tutorials) -->
    <div v-if="showVideo" class="video-section mb-8">
      <div
        class="video-placeholder bg-gray-100 rounded-xl aspect-video flex items-center justify-center border-2 border-dashed border-gray-300"
      >
        <div class="text-center text-gray-500">
          <play-icon class="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p class="font-medium">Video tutorial disponible pronto</p>
          <p class="text-sm">{{ title }}</p>
        </div>
      </div>
    </div>

    <!-- Page Header -->
    <div class="page-header mb-8">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">{{ title }}</h1>
      <p v-if="description" class="text-gray-600 text-lg">{{ description }}</p>
    </div>

    <!-- Main Content -->
    <div class="page-content">
      <slot></slot>
    </div>

    <!-- Related Links -->
    <div v-if="relatedLinks && relatedLinks.length > 0" class="related-links mt-12 pt-8 border-t border-gray-200">
      <h3 class="text-lg font-semibold text-gray-700 mb-4">Temas relacionados</h3>
      <div class="flex flex-wrap gap-3">
        <router-link
          v-for="link in relatedLinks"
          :key="link.route"
          :to="{ name: link.route }"
          class="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-quaternary-100 text-gray-700 hover:text-quaternary-600 rounded-lg transition-colors text-sm font-medium"
        >
          {{ link.label }}
          <arrow-icon class="w-4 h-4 ml-2" />
        </router-link>
      </div>
    </div>

    <!-- Quick Action Button -->
    <div v-if="actionRoute" class="action-button mt-8">
      <router-link
        :to="actionRoute"
        class="inline-flex items-center px-6 py-3 bg-quaternary-500 hover:bg-quaternary-600 text-white rounded-lg transition-colors font-medium shadow-md hover:shadow-lg"
      >
        {{ actionText }}
        <arrow-icon class="w-5 h-5 ml-2" />
      </router-link>
    </div>
  </div>
</template>

<script>
import PlayIcon from "@/icons/PlayIcon.vue";
import ArrowIcon from "@/icons/ArrowIcon.vue";

export default {
  name: "ManualPage",

  components: {
    PlayIcon,
    ArrowIcon,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    showVideo: {
      type: Boolean,
      default: true,
    },
    relatedLinks: {
      type: Array,
      default: () => [],
    },
    actionRoute: {
      type: Object,
      default: null,
    },
    actionText: {
      type: String,
      default: "Ir a la sección",
    },
  },
};
</script>

<style scoped>
.manual-page {
  @apply max-w-4xl;
}

.page-content :deep(h2) {
  @apply text-xl font-semibold text-gray-800 mt-8 mb-4;
}

.page-content :deep(h3) {
  @apply text-lg font-medium text-gray-700 mt-6 mb-3;
}

.page-content :deep(p) {
  @apply text-gray-600 mb-4 leading-relaxed;
}

.page-content :deep(ul) {
  @apply list-disc list-inside text-gray-600 mb-4 space-y-2;
}

.page-content :deep(ol) {
  @apply list-decimal list-inside text-gray-600 mb-4 space-y-2;
}

.page-content :deep(li) {
  @apply leading-relaxed;
}

.page-content :deep(.step-card) {
  @apply bg-white border border-gray-200 rounded-xl p-6 mb-4 shadow-sm;
}

.page-content :deep(.step-number) {
  @apply inline-flex items-center justify-center w-8 h-8 bg-quaternary-500 text-white rounded-full font-bold text-sm mr-3;
}

.page-content :deep(.tip-box) {
  @apply bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4;
}

.page-content :deep(.warning-box) {
  @apply bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-4;
}

.page-content :deep(.info-box) {
  @apply bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg mb-4;
}
</style>
